import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  ArrowLeft, 
  Route, 
  MapPin, 
  Clock, 
  Play, 
  Square, 
  RefreshCw,
  Phone,
  Navigation,
  CheckCircle,
  XCircle,
  Share2,
  MessageCircle,
  Send,
  Star
} from 'lucide-react'
import api from '../../services/api'
import { formatDateBR, formatDateLongBR } from '../../utils/dateManaus'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import toast from 'react-hot-toast'

// Status badge
function StatusBadge({ status }) {
  const statusConfig = {
    aguardando_saida: { label: 'Aguardando', color: 'bg-gray-100 text-gray-700' },
    em_rota: { label: 'Em rota', color: 'bg-blue-100 text-blue-700' },
    entregue: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
    nao_entregue: { label: 'Não entregue', color: 'bg-red-100 text-red-700' },
  }
  const config = statusConfig[status] || statusConfig.aguardando_saida
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  )
}

// Status badge da rota
function RouteStatusBadge({ status }) {
  const statusConfig = {
    planejada: { label: 'Planejada', color: 'bg-yellow-100 text-yellow-700' },
    pausada: { label: 'Aguardando motorista', color: 'bg-orange-100 text-orange-700' },
    em_andamento: { label: 'Em andamento', color: 'bg-blue-100 text-blue-700' },
    finalizada: { label: 'Finalizada', color: 'bg-green-100 text-green-700' },
    cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
  }
  const config = statusConfig[status] || statusConfig.planejada
  return (
    <span className={`px-3 py-1 text-sm font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  )
}

export default function RouteView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [dialogState, setDialogState] = useState(null)

  // Busca dados da rota
  const { data: routeData, isLoading } = useQuery({
    queryKey: ['route', id],
    queryFn: async () => {
      const response = await api.get(`/api/routes/${id}`)
      return response.data.data.route
    },
  })

  // Iniciar rota
  const startRoute = useMutation({
    mutationFn: () => api.post(`/api/routes/${id}/start`),
    onSuccess: () => {
      toast.success('Rota iniciada!')
      queryClient.invalidateQueries(['route', id])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao iniciar rota')
    },
  })

  // Finalizar rota
  const finishRoute = useMutation({
    mutationFn: () => api.post(`/api/routes/${id}/finish`),
    onSuccess: () => {
      toast.success('Rota finalizada!')
      queryClient.invalidateQueries(['route', id])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao finalizar rota')
    },
  })

  // Confirmar entrega
  const confirmDelivery = useMutation({
    mutationFn: (deliveryId) => api.post(`/api/driver/deliveries/${deliveryId}/confirm`),
    onSuccess: () => {
      toast.success('Entrega confirmada!')
      queryClient.invalidateQueries(['route', id])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao confirmar entrega')
    },
  })

  // Marcar como não entregue
  const failDelivery = useMutation({
    mutationFn: ({ deliveryId, reason }) => api.post(`/api/driver/deliveries/${deliveryId}/fail`, { 
      failureReason: reason 
    }), 
    onSuccess: () => {
      toast.success('Entrega marcada como não entregue')
      queryClient.invalidateQueries(['route', id])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar entrega')
    },
  })

  // Solicitar feedback manualmente (BUG-54)
  const requestFeedback = useMutation({
    mutationFn: (deliveryId) => api.post('/api/feedback-requests', { deliveryId }),
    onSuccess: () => {
      toast.success('Solicitação de feedback enviada!')
      queryClient.invalidateQueries({ queryKey: ['feedbackRequests'] })
    },
    onError: (error) => {
      if (error.response?.data?.error === 'ALREADY_EXISTS') {
        toast.error('Já existe uma solicitação de feedback para esta entrega')
      } else {
        toast.error(error.response?.data?.message || 'Erro ao solicitar feedback')
      }
    },
  })

  const handleConfirmDelivery = (deliveryId) => {
    setDialogState({ type: 'confirm', deliveryId })
  }

  const handleFailDelivery = (deliveryId) => {
    setDialogState({ type: 'fail', deliveryId })
  }

  const handleDialogConfirm = (value) => {
    const { type, deliveryId } = dialogState
    setDialogState(null)
    if (type === 'confirm') {
      confirmDelivery.mutate(deliveryId)
    } else {
      failDelivery.mutate({ deliveryId, reason: value })
    }
  }

  // Reotimizar rota
  const reoptimize = useMutation({
    mutationFn: () => api.post(`/api/routes/${id}/reoptimize`),
    onSuccess: () => {
      toast.success('Rota reotimizada!')
      queryClient.invalidateQueries(['route', id])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao reotimizar')
    },
  })

  // Gera (ou regenera) o link público de acesso do motorista e já compartilha
  // via WhatsApp - mesma técnica de montar a URL do shareWhatsApp logo abaixo.
  const sendDriverLink = useMutation({
    mutationFn: () => api.post(`/api/routes/${id}/send-driver-link`),
    onSuccess: (response) => {
      const token = response.data.data.token
      const link = `${window.location.origin}/driver-link/${token}`
      const text = `*Link da sua rota de entregas*\n\n${link}`
      // Com motorista atribuído, pré-preenche o número (dígito puro, sem DDI,
      // como já vem do backend) - sem motorista, mantém o comportamento atual
      // de deixar o vendedor escolher o contato manualmente.
      const phone = routeData?.driver?.phone
      const whatsappUrl = phone
        ? `https://wa.me/55${phone}?text=${encodeURIComponent(text)}`
        : `https://wa.me/?text=${encodeURIComponent(text)}`
      window.open(whatsappUrl, '_blank')
      toast.success('Link gerado! Abrindo WhatsApp...')
      queryClient.invalidateQueries(['route', id])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao gerar link')
    },
  })

  // Compartilhar via WhatsApp
  const shareWhatsApp = () => {
    if (!routeData?.deliveries?.length) return

    const deliveries = [...routeData.deliveries]
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder)

    // Formatar data
    const date = formatDateBR(routeData.scheduledDate) || 'Hoje'

    // Montar texto (usando emojis simples que funcionam em URLs)
    let text = `*Rota de Entregas - ${date}*\n\n`

    deliveries.forEach((d, i) => {
      const addr = d.recipient?.address
      const street = addr?.street || d.recipient?.addressStreet || ''
      const number = addr?.number || d.recipient?.addressNumber || ''
      const neighborhood = addr?.neighborhood || d.recipient?.addressNeighborhood || ''
      text += `*${i + 1}. ${d.recipient?.name}*\n`
      text += `${street}, ${number} - ${neighborhood}\n`
      if (d.estimatedArrival) {
        text += `Previsao: ${d.estimatedArrival.substring(0, 5)}\n`
      }
      text += `\n`
    })

    // Adicionar link do Maps
    const origin = routeData.origin 
      ? `${routeData.origin.lat},${routeData.origin.lng}`
      : null
    const coords = deliveries.map(d => {
      const lat = d.recipient?.coordinates?.lat || d.recipient?.lat
      const lng = d.recipient?.coordinates?.lng || d.recipient?.lng
      return `${lat},${lng}`
    })
    const destination = coords[coords.length - 1]
    const waypoints = coords.slice(0, -1).join('|')
    
    let mapsUrl = 'https://www.google.com/maps/dir/?api=1'
    if (origin) mapsUrl += `&origin=${origin}`
    mapsUrl += `&destination=${destination}`
    if (waypoints) mapsUrl += `&waypoints=${waypoints}`

    text += `*Abrir no Maps:*\n${mapsUrl}`

    // Abrir WhatsApp
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`
    window.open(whatsappUrl, '_blank')
  }

  // Abrir no Waze (primeira entrega pendente ou primeira da lista)
  // NOTA: Waze só aceita 1 destino por vez, não suporta múltiplos waypoints
  const openWaze = () => {
    if (!routeData?.deliveries?.length) return

    const deliveries = [...routeData.deliveries]
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder)

    // Encontrar primeira entrega pendente ou usar a primeira
    const nextDelivery = deliveries.find(d => 
      d.status === 'aguardando_saida' || d.status === 'em_rota'
    ) || deliveries[0]

    const lat = nextDelivery.recipient?.coordinates?.lat || nextDelivery.recipient?.lat
    const lng = nextDelivery.recipient?.coordinates?.lng || nextDelivery.recipient?.lng
    const name = nextDelivery.recipient?.name || 'Destino'

    // Mostrar toast informando qual entrega vai abrir
    toast.success(`Abrindo Waze: ${name}`)

    // URL do Waze com navegação
    const wazeUrl = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`
    window.open(wazeUrl, '_blank')
  }

  // Abre navegação no Google Maps
  const openNavigation = () => {
    if (!routeData?.deliveries?.length) return
    
    // Ordena entregas pela sequência
    const deliveries = [...routeData.deliveries]
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
    
    // Origem = loja do vendedor
    const origin = routeData.origin 
      ? `${routeData.origin.lat},${routeData.origin.lng}`
      : null
    
    // Pega coordenadas de cada entrega
    const coords = deliveries.map(d => {
      const lat = d.recipient?.coordinates?.lat || d.recipient?.lat
      const lng = d.recipient?.coordinates?.lng || d.recipient?.lng
      return `${lat},${lng}`
    })
    
    // Última entrega é o destino final
    const destination = coords[coords.length - 1]
    
    // Todas as outras são waypoints (exceto a última)
    const waypoints = coords.slice(0, -1).join('|')
    
    // Monta URL
    let url = 'https://www.google.com/maps/dir/?api=1'
    
    if (origin) {
      url += `&origin=${origin}`
    }
    
    url += `&destination=${destination}`
    
    if (waypoints) {
      url += `&waypoints=${waypoints}`
    }
    
    window.open(url, '_blank')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const route = routeData

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button 
          onClick={() => navigate('/routes')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="page-title">
              Rota #{route?.id?.slice(-6).toUpperCase()}
            </h1>
            <RouteStatusBadge status={route?.status} />
          </div>
          <p className="page-subtitle">
            {formatDateLongBR(route?.scheduledDate)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de entregas */}
        <div className="lg:col-span-2">
          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">
              Entregas ({route?.deliveries?.length || 0})
            </h3>

            <div className="space-y-3">
              {route?.deliveries
                ?.sort((a, b) => a.sequenceOrder - b.sequenceOrder)
                .map((delivery, index) => (
                  <div
                    key={delivery.id}
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
                  >
                    {/* Número da ordem */}
                    <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                      {delivery.sequenceOrder || index + 1}
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">
                          {delivery.recipient?.name}
                        </p>
                        <StatusBadge status={delivery.status} />
                      </div>
                      <p className="text-sm text-gray-500">
                        {delivery.recipient?.address?.street || delivery.recipient?.addressStreet}, {delivery.recipient?.address?.number || delivery.recipient?.addressNumber} - {delivery.recipient?.address?.neighborhood || delivery.recipient?.addressNeighborhood}
                      </p>
                      {delivery.estimatedArrival && (
                        <p className="text-xs text-primary-600 mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          Previsão: {delivery.estimatedArrival.substring(0, 5)}
                        </p>
                      )}
                    </div>

                    {/* Ações */}
                    {/* Ações */}
                    <div className="flex gap-2">
                      <a
                        href={`tel:${delivery.recipient?.phone}`}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                      </a>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${delivery.recipient?.coordinates?.lat || delivery.recipient?.lat},${delivery.recipient?.coordinates?.lng || delivery.recipient?.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <MapPin className="w-4 h-4" />
                      </a>
                      {route?.status === 'em_andamento' && delivery.status === 'em_rota' && (
                        <>
                          <button
                            onClick={() => handleConfirmDelivery(delivery.id)}
                            className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Confirmar entrega"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleFailDelivery(delivery.id)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Não entregue"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {delivery.status === 'entregue' && (
                        <button
                          onClick={() => requestFeedback.mutate(delivery.id)}
                          disabled={requestFeedback.isPending}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Solicitar feedback"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </Card>
        </div>

        {/* Painel lateral */}
        <div>
          <Card className="sticky top-24">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-primary-100 rounded-lg">
                <Route className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Resumo</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Entregas</span>
                <span className="font-semibold text-gray-900">
                  {route?.deliveries?.length || 0}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Distância total</span>
                <span className="font-semibold text-gray-900">
                  {route?.totalDistance 
                    ? `${(route.totalDistance / 1000).toFixed(1)} km`
                    : '--'}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Tempo estimado</span>
                <span className="font-semibold text-gray-900">
                  {route?.totalDuration
                    ? `${Math.round(route.totalDuration / 60)} min`
                    : '--'}
                </span>
              </div>
              {route?.driver && (
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Motorista</span>
                  <span className="font-semibold text-gray-900">
                    {route.driver.name}
                  </span>
                </div>
              )}
            </div>

            {/* Ações baseadas no status */}
            <div className="space-y-3">
              {route?.status === 'planejada' && (
                <>
                  <Button 
                    fullWidth 
                    onClick={() => startRoute.mutate()}
                    isLoading={startRoute.isPending}
                  >
                    <Play className="w-4 h-4" />
                    Iniciar Rota
                  </Button>
                  <Button
                    fullWidth
                    variant="secondary"
                    onClick={() => reoptimize.mutate()}
                    isLoading={reoptimize.isPending}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reotimizar
                  </Button>
                  <Button
                    fullWidth
                    variant="secondary"
                    onClick={() => sendDriverLink.mutate()}
                    isLoading={sendDriverLink.isPending}
                  >
                    <Send className="w-4 h-4" />
                    Enviar para entregador
                  </Button>
                </>
              )}

              {route?.status === 'pausada' && (
                <>
                  <div className="text-center py-4">
                    <p className="text-orange-600 font-medium">Link enviado, aguardando o motorista abrir</p>
                  </div>
                  <Button
                    fullWidth
                    variant="secondary"
                    onClick={() => sendDriverLink.mutate()}
                    isLoading={sendDriverLink.isPending}
                  >
                    <Send className="w-4 h-4" />
                    Reenviar link
                  </Button>
                </>
              )}

              {(route?.status === 'planejada' || route?.status === 'em_andamento') && (
                <>
                  <Button 
                    fullWidth 
                    variant={route?.status === 'planejada' ? 'secondary' : 'primary'}
                    onClick={openNavigation}
                  >
                    <Navigation className="w-4 h-4" />
                    Abrir no Google Maps
                  </Button>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      variant="secondary"
                      onClick={openWaze}
                      className="text-sm"
                      title="Abre navegação para a próxima entrega"
                    >
                      <MapPin className="w-4 h-4" />
                      Waze
                    </Button>
                    <Button 
                      variant="secondary"
                      onClick={shareWhatsApp}
                      className="text-sm"
                    >
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp
                    </Button>
                  </div>
                </>
              )}

              {route?.status === 'em_andamento' && (
                <Button 
                  fullWidth 
                  variant="secondary"
                  onClick={() => finishRoute.mutate()}
                  isLoading={finishRoute.isPending}
                >
                  <Square className="w-4 h-4" />
                  Finalizar Rota
                </Button>
              )}

              {route?.status === 'finalizada' && (
                <div className="text-center py-4">
                  <p className="text-green-600 font-medium">✓ Rota concluída</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {route.finishedAt && `Finalizada em ${new Date(route.finishedAt).toLocaleString('pt-BR')}`}
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={!!dialogState}
        title={dialogState?.type === 'fail' ? 'Não entregue' : 'Confirmar entrega'}
        message={dialogState?.type === 'fail' ? 'Motivo da não entrega:' : 'Confirmar esta entrega?'}
        showInput={dialogState?.type === 'fail'}
        inputDefaultValue="Cliente ausente"
        onConfirm={handleDialogConfirm}
        onCancel={() => setDialogState(null)}
      />
    </div>
  )
}
